import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { getUserDisplayName } from '@client/components/users/user-detail/UserDetailHeader';
import { UserDetailPage } from '@client/components/users/user-detail/UserDetailPage';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import type { ShareableRouteContext } from '@client/types/router';
import { ORG_ROLES } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/users/details/$userId')({
    staticData: {
        ui: {
            widthVariant: 'default',
        },
    },
    params: {
        parse({ userId }) {
            return { userId: BigInt(userId) };
        },
    },
    beforeLoad(ctx): ShareableRouteContext {
        // Dynamic breadcrumb: the user's name (getUserDisplayName falls back to email when unnamed).
        async function breadcrumb() {
            const user = await ctx.context.trpcUtils.user.getUser.ensureData({
                id: ctx.params.userId,
                orgId: ctx.params.orgId,
                include: ['handlingPartyIds', 'trainingManagerHandlingPartyIds'],
            });
            return getUserDisplayName(user);
        }

        return {
            breadcrumb: { title: breadcrumb },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <UserDetail />
        </PageController>
    );
}

const USERS_LIST_ROUTE = '/console/$orgId/configuration/org-settings/users';

function UserDetail() {
    const orgId = useOrgId();
    const org = useOrg();
    const { userId } = Route.useParams();
    const currentUser = useUser();
    const { can } = usePermissions();
    const navigate = useNavigate();
    const isAdmin = hasPermission(currentUser.roles, 'super:admin') || can('org:admin');

    return (
        <UserDetailPage
            orgId={orgId}
            userId={userId}
            enabledModules={org.enabledModules}
            access={{
                scope: 'org',
                isAdmin,
                canModifyRoles: isAdmin || currentUser.roles.includes(ORG_ROLES.USER_MANAGER),
            }}
            goToList={() => navigate({ to: USERS_LIST_ROUTE, params: { orgId } })}
            backLink={
                <BackLink to={USERS_LIST_ROUTE} params={{ orgId }}>
                    Back to team
                </BackLink>
            }
            documentTitleFallback="Team member"
            activeSupportSessionAction={
                <Button.Link
                    variant="secondary"
                    size="small"
                    to="/console/$orgId/configuration/org-settings/support-sessions"
                    params={{ orgId }}
                >
                    Go to support sessions
                </Button.Link>
            }
        />
    );
}
